import React, { useState, useEffect } from "react";
import { Checkbox, Popover, Button, Badge, Tooltip } from "antd";
import { LayoutOutlined } from "@ant-design/icons";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";

export const ResizableTitle: React.FC<any> = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  const isFixed =
    restProps.className?.includes("ant-table-cell-fix-left") ||
    restProps.className?.includes("ant-table-cell-fix-right") ||
    restProps.style?.position === "sticky";

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        {...restProps}
        style={{
          ...restProps.style,
          position: isFixed ? "sticky" : "relative",
        }}
      />
    </Resizable>
  );
};

interface UseResizableColumnsProps {
  columns: any[];
  storageKey: string;
}

export function useResizableColumns({
  columns: rawColumns,
  storageKey,
}: UseResizableColumnsProps) {
  // Inject custom styles for drag handles
  useEffect(() => {
    const styleId = "use-resizable-columns-styles";
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement("style");
      styleEl.id = styleId;
      styleEl.innerHTML = `
        .react-resizable {
          position: relative;
          background-clip: padding-box;
        }
        .react-resizable-handle {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 8px;
          height: 100%;
          cursor: col-resize;
          z-index: 1;
          transition: border-right-color 0.2s, background-color 0.2s;
        }
        .react-resizable-handle:hover {
          border-right: 3px solid var(--primary-color, #1677ff);
          background-color: rgba(22, 119, 255, 0.08);
        }
      `;
      document.head.appendChild(styleEl);
    }
  }, []);

  // Determine toggleable columns based on column metadata
  const toggleableColumns = rawColumns
    .filter((col) => col.key && col.toggleable !== false)
    .map((col) => ({
      key: col.key,
      label: col.title || col.key,
      group: col?.group,
    }));

  // Initialize column widths state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => {
      const saved = localStorage.getItem(`${storageKey}_column_widths`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          // Fallback
        }
      }
      // Gather default widths from rawColumns
      const defaults: Record<string, number> = {};
      rawColumns.forEach((col) => {
        if (col.key) {
          defaults[col.key] = col.width || 120; // Default fallback width
        }
      });
      return defaults;
    },
  );

  // Initialize column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(`${storageKey}_visible_columns`);
    const allToggleableKeys = toggleableColumns.map((c) => c.key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Include any newly added toggleable keys missing from saved configuration
          const missingKeys = allToggleableKeys.filter(
            (k) => !parsed.includes(k),
          );
          if (missingKeys.length > 0) {
            const updated = [...parsed, ...missingKeys];
            localStorage.setItem(
              `${storageKey}_visible_columns`,
              JSON.stringify(updated),
            );
            return updated;
          }
          return parsed;
        }
      } catch (e) {
        // Fallback
      }
    }
    return allToggleableKeys;
  });

  // Re-sync widths/visibility when storageKey changes (e.g. switching category) -
  // useState initializers only run on mount, so a changed storageKey wouldn't otherwise reload
  useEffect(() => {
    const allToggleableKeys = rawColumns
      .filter((col) => col.key && col.toggleable !== false)
      .map((col) => col.key);

    const savedWidths = localStorage.getItem(`${storageKey}_column_widths`);
    if (savedWidths) {
      try {
        setColumnWidths(JSON.parse(savedWidths));
      } catch (e) {
        setColumnWidths({});
      }
    } else {
      const defaults: Record<string, number> = {};
      rawColumns.forEach((col) => {
        if (col.key) defaults[col.key] = col.width || 120;
      });
      setColumnWidths(defaults);
    }

    const savedVisible = localStorage.getItem(`${storageKey}_visible_columns`);
    if (savedVisible) {
      try {
        const parsed = JSON.parse(savedVisible);
        setVisibleColumns(Array.isArray(parsed) ? parsed : allToggleableKeys);
      } catch (e) {
        setVisibleColumns(allToggleableKeys);
      }
    } else {
      setVisibleColumns(allToggleableKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const handleResize =
    (key: string) =>
    (_: any, { size }: { size: { width: number } }) => {
      setColumnWidths((prev) => {
        const updated = { ...prev, [key]: size.width };
        localStorage.setItem(
          `${storageKey}_column_widths`,
          JSON.stringify(updated),
        );
        return updated;
      });
    };

  const handleColumnVisibilityChange = (checkedValues: string[]) => {
    setVisibleColumns(checkedValues);
    localStorage.setItem(
      `${storageKey}_visible_columns`,
      JSON.stringify(checkedValues),
    );
  };

  // Construct final columns
  const filteredColumns = rawColumns.filter(
    (col) =>
      !col.key || col.toggleable === false || visibleColumns.includes(col.key),
  );

  const finalColumns = filteredColumns.map((col) => {
    if (!col.key || col.resizable === false) {
      return col;
    }
    const width = columnWidths[col.key] || col.width || 120;
    return {
      ...col,
      width,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: handleResize(col.key),
      }),
    };
  });

  // Construct customizer component
  const columnSelector = (
    <Popover
      content={
        <div
          style={{
            width: 200,
            maxHeight: "min(415px, calc(100vh - 120px))",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 8,
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Show/hide columns
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {toggleableColumns.map((col) => (
              <>
                {col.group && (
                  <div
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      paddingBottom: 0,
                      marginBottom: 4,
                      fontWeight: 600,
                    }}
                  >
                    {col.group}
                  </div>
                )}

                <Checkbox
                  key={col.key}
                  checked={visibleColumns.includes(col.key)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleColumnVisibilityChange(
                      checked
                        ? [...visibleColumns, col.key]
                        : visibleColumns.filter((k) => k !== col.key),
                    );
                  }}
                >
                  {col.label}
                </Checkbox>
              </>
            ))}
          </div>
        </div>
      }
      trigger="click"
      placement="bottomRight"
    >
      <Tooltip title="Show/hide columns">
        <Badge
          count={toggleableColumns.length - visibleColumns.length}
          size="small"
          offset={[-2, 2]}
        >
          <Button icon={<LayoutOutlined />} />
        </Badge>
      </Tooltip>
    </Popover>
  );

  return {
    columns: finalColumns,
    components: {
      header: {
        cell: ResizableTitle,
      },
    },
    columnSelector,
  };
}
